import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Checkbox,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../globals/utils/api";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setCarrierId, setStateId, setBondTypeId } from "../../../../store/slices/premiumSlice";
import NumberFormatCustom from "../../../../globals/utils/Bond Creation Utils/numberFormatCustom";

function PremiumManager() {
  const dispatch = useDispatch();
  const { carrierId, stateId, bondTypeId } = useSelector((state) => state.premium);
  const [carriers, setCarriers] = useState([]);
  const [states, setStates] = useState([]);
  const [bondTypes, setBondTypes] = useState([]);
  const carrier = carrierId;
  const state = stateId;
  const bondType = bondTypeId;
  const [showTable, setShowTable] = useState(false);
  const [scales, setScales] = useState([]);
  const [openScaleDialog, setOpenScaleDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentScaleId, setCurrentScaleId] = useState(null);
  const [min, setMin] = useState();
  const [max, setMax] = useState();
  const [perAnnum, setPerAnnum] = useState();
  const [additionalPerM, setAdditionalPerM] = useState();
  const [minimum, setMinimum] = useState();
  const [effectiveDate, setEffectiveDate] = useState();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingBondTypes, setLoadingBondTypes] = useState(true);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [selectedTargetStates, setSelectedTargetStates] = useState([]);
  const [copying, setCopying] = useState(false);
  const [openCopyBondTypeDialog, setOpenCopyBondTypeDialog] = useState(false);
  const [sourceBondTypeId, setSourceBondTypeId] = useState("");
  const [targetBondTypeId, setTargetBondTypeId] = useState("");
  const [copyingBondTypes, setCopyingBondTypes] = useState(false);

  const formatDateUS = (value) => {
    if (!value) return "";
    const isoLike = /^\d{4}-\d{2}-\d{2}/;
    if (isoLike.test(value)) {
      const [y, m, d] = value.substring(0, 10).split("-");
      return `${m}/${d}/${y}`;
    }
    const dt = new Date(value);
    if (isNaN(dt)) return value;
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (isNaN(num)) return "-";
    return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const bondTypeName = bondTypes.find((option) => option.bond_type_id === Number(bondType))?.type || "-";

  useEffect(() => {
    fetchCarriers();
    fetchStates();
    fetchBondTypes();
  }, []);

  const fetchCarriers = async () => {
    try {
      setLoadingCarriers(true);
      const response = await apiClient("/carriers/0/", "GET");
      setCarriers(response.message === "Carriers retrieved successfully." ? response.data : []);
    } catch (error) {
      console.error("Error fetching carriers:", error);
    } finally {
      setLoadingCarriers(false);
    }
  };

  const fetchStates = async () => {
    try {
      setLoadingStates(true);
      const response = await apiClient("/states/0/", "GET");
      setStates(response || []);
    } catch (error) {
      console.error("Error fetching states:", error);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchBondTypes = async () => {
    try {
      setLoadingBondTypes(true);
      const response = await apiClient("/bondtypes/", "GET");
      setBondTypes(response || []);
    } catch (error) {
      console.error("Error fetching bond types:", error);
    } finally {
      setLoadingBondTypes(false);
    }
  };

  const handleViewScale = async () => {
    setLoading(true);
    try {
      const response = await apiClient(
        `/premiums/?bond_carrier_id=${carrier}&bond_state_id=${state}&bond_type_id=${bondType}`,
        "GET"
      );
      setScales(response || []);
      setShowTable(true);
    } catch (error) {
      console.error("Error fetching premium scales:", error);
      toast.error("Failed to load premium scales.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddScale = async () => {
    setSubmitting(true);
    const newScale = {
      bond_carrier_id: carrier,
      bond_state_id: state,
      bond_type_id: bondType,
      min,
      max,
      per_anum: perAnnum,
      additional_per_m: additionalPerM,
      minimum,
      effective_date: effectiveDate,
    };
    try {
      await apiClient("/premiums/", "POST", newScale);
      toast.success("Premium scale added successfully.");
      handleViewScale();
    } catch (error) {
      console.error("Error adding scale:", error);
      toast.error("Failed to add premium scale.");
    } finally {
      setSubmitting(false);
      setOpenScaleDialog(false);
    }
  };

  const handleEditScale = async (scale) => {
    setIsEditMode(true);
    setCurrentScaleId(scale.bond_scale.bond_scale_id);
    setMin(scale.bond_scale.min);
    setMax(scale.bond_scale.max);
    setPerAnnum(scale.bond_scale.per_anum);
    setAdditionalPerM(scale.bond_scale.additional_per_m);
    setMinimum(scale.bond_scale.minimum);
    setEffectiveDate(scale.bond_scale.effective_date);
    setOpenScaleDialog(true);
  };

  const handleUpdateScale = async () => {
    setSubmitting(true);
    const updatedScale = {
      min,
      max,
      per_anum: perAnnum,
      additional_per_m: additionalPerM,
      minimum,
      effective_date: effectiveDate,
    };
    try {
      await apiClient(`/premiums/${currentScaleId}/`, "PATCH", updatedScale);
      toast.success("Premium scale updated successfully.");
      handleViewScale();
    } catch (error) {
      console.error("Error updating scale:", error);
      toast.error("Failed to update premium scale.");
    } finally {
      setSubmitting(false);
      setOpenScaleDialog(false);
      setIsEditMode(false);
    }
  };

  const handleDeleteScale = async (bond_scale_id) => {
    setSubmitting(true);
    try {
      await apiClient(`/premiums/${bond_scale_id}/`, "DELETE");
      toast.success("Premium scale deleted successfully.");
      handleViewScale();
    } catch (error) {
      console.error("Error deleting scale:", error);
      toast.error("Failed to delete premium scale.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
          Select Carrier, State, and Bond Type
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {loadingCarriers ? (
              <CircularProgress />
            ) : (
              <TextField
                select
                fullWidth
                label="Carrier"
                value={carrier}
                onChange={(e) => dispatch(setCarrierId(e.target.value))}
                variant="outlined"
              >
                {carriers.map((option) => (
                  <MenuItem key={option.bond_carrier_id} value={option.bond_carrier_id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {loadingStates ? (
              <CircularProgress />
            ) : (
              <TextField
                select
                fullWidth
                label="State"
                value={state}
                onChange={(e) => dispatch(setStateId(e.target.value))}
                variant="outlined"
              >
                {states.map((option) => (
                  <MenuItem key={option.bond_state_id} value={option.bond_state_id}>
                    {option.state_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {loadingBondTypes ? (
              <CircularProgress />
            ) : (
              <TextField
                select
                fullWidth
                label="Bond Type"
                value={bondType}
                onChange={(e) => dispatch(setBondTypeId(e.target.value))}
                variant="outlined"
              >
                {bondTypes.map((option) => (
                  <MenuItem key={option.bond_type_id} value={option.bond_type_id}>
                    {option.type}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewScale}
            disabled={!carrier || !state || !bondType}
          >
            View Scale
          </Button>
        </Box>

        {showTable && (
          <>
            <Typography variant="h5" gutterBottom sx={{ marginTop: "20px" }}>
              Scale Values
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenCopyBondTypeDialog(true)}
                sx={{ mr: 1 }}
                disabled={!state || bondTypes.length === 0}
              >
                Copy Premiums to Bond Types
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenCopyDialog(true)}
                sx={{ mr: 1 }}
                disabled={!state || states.length === 0}
              >
                Copy Premiums to States
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setOpenScaleDialog(true);
                  setIsEditMode(false);
                }}
              >
                Add Scale Value
              </Button>
            </Box>

            {loading ? (
              <CircularProgress />
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                      <TableCell><strong>Bond Type</strong></TableCell>
                      <TableCell><strong>Min</strong></TableCell>
                      <TableCell><strong>Max</strong></TableCell>
                      <TableCell align="right">Per Annum</TableCell>
                      <TableCell align="right">Additional Per M</TableCell>
                      <TableCell align="right">Minimum</TableCell>
                      <TableCell align="right">Effective Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scales.map((scale) => (
                      <TableRow key={scale.bond_scale?.bond_scale_id ?? scale.bond_scale_id}>
                        <TableCell>{bondTypeName}</TableCell>
                        <TableCell>{formatCurrency(scale.bond_scale.min)}</TableCell>
                        <TableCell>{formatCurrency(scale.bond_scale.max)}</TableCell>
                        <TableCell align="right">{formatCurrency(scale.bond_scale.per_anum)}</TableCell>
                        <TableCell align="right">{formatCurrency(scale.bond_scale.additional_per_m)}</TableCell>
                        <TableCell align="right">{formatCurrency(scale.bond_scale.minimum)}</TableCell>
                        <TableCell align="right">{formatDateUS(scale.bond_scale.effective_date)}</TableCell>
                        <TableCell align="center">
                          <IconButton color="primary" onClick={() => handleEditScale(scale)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDeleteScale(scale.bond_scale?.bond_scale_id ?? scale.bond_scale_id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      <Dialog open={openScaleDialog} onClose={() => setOpenScaleDialog(false)}>
        <DialogTitle>{isEditMode ? "Edit Scale Value" : "Add New Scale Value"}</DialogTitle>
        <DialogContent sx={{ width: "400px" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Min"
            fullWidth
            variant="outlined"
            value={min ?? ""}
            onChange={(e) => setMin(e.target.value)}
            placeholder="-"
            name="min"
            InputProps={{ inputComponent: NumberFormatCustom }}
          />
          <TextField
            margin="dense"
            label="Max"
            fullWidth
            variant="outlined"
            value={max ?? ""}
            onChange={(e) => setMax(e.target.value)}
            placeholder="-"
            name="max"
            InputProps={{ inputComponent: NumberFormatCustom }}
          />
          <TextField
            margin="dense"
            label="Per Annum"
            fullWidth
            variant="outlined"
            value={perAnnum ?? ""}
            onChange={(e) => setPerAnnum(e.target.value)}
            placeholder="-"
            name="perAnnum"
            InputProps={{ inputComponent: NumberFormatCustom }}
          />
          <TextField
            margin="dense"
            label="Additional Per M"
            fullWidth
            variant="outlined"
            value={additionalPerM ?? ""}
            onChange={(e) => setAdditionalPerM(e.target.value)}
            placeholder="-"
            name="additionalPerM"
            InputProps={{ inputComponent: NumberFormatCustom }}
          />
          <TextField
            margin="dense"
            label="Minimum"
            fullWidth
            variant="outlined"
            value={minimum ?? ""}
            onChange={(e) => setMinimum(e.target.value)}
            placeholder="-"
            name="minimum"
            InputProps={{ inputComponent: NumberFormatCustom }}
          />
          <TextField
            margin="dense"
            label="Effective Date"
            type="date"
            fullWidth
            variant="outlined"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScaleDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={isEditMode ? handleUpdateScale : handleAddScale}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : isEditMode ? "Update Scale Value" : "Add Scale Value"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCopyDialog} onClose={() => setOpenCopyDialog(false)}>
        <DialogTitle>Copy Premiums to Target States</DialogTitle>
        <DialogContent sx={{ width: "420px" }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Source state: {states.find((s) => s.bond_state_id === state)?.state_name || "(none)"}
          </Typography>
          <TextField
            select
            fullWidth
            label="Target States"
            value={selectedTargetStates}
            onChange={(e) => setSelectedTargetStates(e.target.value)}
            variant="outlined"
            SelectProps={{ multiple: true, renderValue: (selected) => {
              const labels = states
                .filter((s) => selected.includes(s.bond_state_id))
                .map((s) => s.state_name);
              return labels.join(", ");
            } }}
          >
            {states
              .filter((option) => option.bond_state_id !== state)
              .map((option) => (
                <MenuItem key={option.bond_state_id} value={option.bond_state_id}>
                  <Checkbox checked={selectedTargetStates.indexOf(option.bond_state_id) > -1} />
                  <ListItemText primary={option.state_name} />
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCopyDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                setCopying(true);
                await apiClient(
                  "/copy-premiums/",
                  "POST",
                  {
                    source_state_id: Number(state),
                    target_state_ids: selectedTargetStates.map((id) => Number(id)),
                  }
                );
                toast.success("Premiums copied successfully.");
                setOpenCopyDialog(false);
                setSelectedTargetStates([]);
              } catch (error) {
                console.error("Error copying premiums:", error);
                toast.error("Failed to copy premiums.");
              } finally {
                setCopying(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={copying || !state || selectedTargetStates.length === 0}
          >
            {copying ? <CircularProgress size={24} /> : "Copy"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCopyBondTypeDialog} onClose={() => setOpenCopyBondTypeDialog(false)}>
        <DialogTitle>Copy Premiums Between Bond Types (Within State)</DialogTitle>
        <DialogContent sx={{ width: "420px" }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            State: {states.find((s) => s.bond_state_id === state)?.state_name || "(none)"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Source Bond Type"
                value={sourceBondTypeId}
                onChange={(e) => setSourceBondTypeId(e.target.value)}
                variant="outlined"
              >
                {bondTypes.map((option) => (
                  <MenuItem key={option.bond_type_id} value={option.bond_type_id}>
                    {option.type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Target Bond Type"
                value={targetBondTypeId}
                onChange={(e) => setTargetBondTypeId(e.target.value)}
                variant="outlined"
              >
                {bondTypes.map((option) => (
                  <MenuItem key={option.bond_type_id} value={option.bond_type_id}>
                    {option.type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCopyBondTypeDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                setCopyingBondTypes(true);
                await apiClient(
                  "/copy-premiums-between-bond-types/",
                  "POST",
                  {
                    state_id: Number(state),
                    source_bond_type_id: Number(sourceBondTypeId),
                    target_bond_type_id: Number(targetBondTypeId),
                  }
                );
                toast.success("Premiums copied between bond types successfully.");
                setOpenCopyBondTypeDialog(false);
                // If current selected bond type equals target, refresh
                if (Number(targetBondTypeId) === Number(bondType)) {
                  handleViewScale();
                }
                setSourceBondTypeId("");
                setTargetBondTypeId("");
              } catch (error) {
                console.error("Error copying premiums between bond types:", error);
                toast.error("Failed to copy premiums between bond types.");
              } finally {
                setCopyingBondTypes(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={
              copyingBondTypes || !state || !sourceBondTypeId || !targetBondTypeId || Number(sourceBondTypeId) === Number(targetBondTypeId)
            }
          >
            {copyingBondTypes ? <CircularProgress size={24} /> : "Copy"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PremiumManager;
